import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { promptForPushIfMeaningful } from "../utils/pushNotifications";

const FavoritesContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FavoritesProvider({ children }) {
  const { token, isLoggedIn, user } = useAuth();
  const [favIds, setFavIds] = useState(new Set());
  const [favAds, setFavAds] = useState([]);
  const [loading, setLoading] = useState(false);
  // iter352 — `loaded` = at least one successful fetch (Saved page must not
  // flash the empty state before the first response). `error` powers the
  // retry UI on the Saved page.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Fetch favorites from API
  const refreshFavorites = useCallback(async () => {
    if (!isLoggedIn || !token) {
      // Load from localStorage for non-logged in users
      try {
        const saved = localStorage.getItem('favorites');
        if (saved) {
          const ids = JSON.parse(saved);
          setFavIds(new Set(ids));
        }
      } catch (e) {
        console.error('Failed to load local favorites:', e);
      }
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/me/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const ads = data?.favorites || data?.items || [];
        setFavAds(ads);
        // Extract ad IDs from the favorites
        const ids = ads.map(fav => {
          // Handle both formats: { ad: { ad_id } } or { ad_id }
          const ad = fav.ad || fav;
          return String(ad.ad_id || ad._id || ad.id);
        }).filter(Boolean);
        setFavIds(new Set(ids));
        setLoaded(true);
      } else {
        setError('fetch_failed');
      }
    } catch (e) {
      console.error('Failed to fetch favorites:', e);
      setError('network');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, token]);

  // Load favorites on mount and when auth changes
  useEffect(() => {
    if (!isLoggedIn) {
      // Clear in-memory favorites on logout — prevents leakage to next user
      setFavIds(new Set());
      setFavAds([]);
      setLoaded(false);
      setError(null);
      return;
    }
    refreshFavorites();
  }, [refreshFavorites, isLoggedIn]);

  // Add to favorites
  const addFavorite = useCallback(async (adId) => {
    if (!isLoggedIn || !token) {
      // Guest: store in localStorage only.  favAds intentionally not populated
      // for guests — the Saved page gates on isLoggedIn anyway.
      const newIds = new Set(favIds);
      newIds.add(String(adId));
      setFavIds(newIds);
      localStorage.setItem('favorites', JSON.stringify([...newIds]));
      return { success: true };
    }

    try {
      const response = await fetch(`${API_URL}/api/me/favorites/${adId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        // Optimistic favIds update (heart turns red immediately)
        const newIds = new Set(favIds);
        newIds.add(String(adId));
        setFavIds(newIds);
        // Re-sync favAds from backend so the Saved page reflects the new
        // entry IMMEDIATELY (single source of truth: the API).
        refreshFavorites();
        // Push-notification opt-in trigger #1: meaningful action = saving
        // an ad. Helper is one-shot guarded — never re-prompts the same
        // device. If permission/sw isn't available it silently no-ops.
        promptForPushIfMeaningful({
          userId: user?.user_id,
          token,
          language: document.documentElement.lang === 'ar' ? 'ar' : 'en',
        }).catch(() => { /* never let push surface errors to favorites flow */ });
        return { success: true };
      }
      return { success: false };
    } catch (e) {
      console.error('Failed to add favorite:', e);
      return { success: false, error: e.message };
    }
  }, [isLoggedIn, token, user, favIds, refreshFavorites]);

  // Remove from favorites
  const removeFavorite = useCallback(async (adId) => {
    if (!isLoggedIn || !token) {
      const newIds = new Set(favIds);
      newIds.delete(String(adId));
      setFavIds(newIds);
      localStorage.setItem('favorites', JSON.stringify([...newIds]));
      return { success: true };
    }

    try {
      const response = await fetch(`${API_URL}/api/me/favorites/${adId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        // Optimistic favIds update
        const newIds = new Set(favIds);
        newIds.delete(String(adId));
        setFavIds(newIds);
        // Optimistic favAds update — drop the ad so the Saved page reflects
        // the removal instantly without waiting for the GET round-trip.
        setFavAds((prev) => prev.filter((entry) => {
          const ad = entry?.ad || entry;
          return String(ad?.ad_id || ad?._id || ad?.id) !== String(adId);
        }));
        // Re-sync to be safe (handles aliasing edge cases)
        refreshFavorites();
        return { success: true };
      }
      return { success: false };
    } catch (e) {
      console.error('Failed to remove favorite:', e);
      return { success: false, error: e.message };
    }
  }, [isLoggedIn, token, favIds, refreshFavorites]);

  // Toggle favorite (add if not exists, remove if exists)
  const toggleFavorite = useCallback(async (adId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const adIdStr = String(adId);
    const isFav = favIds.has(adIdStr);
    
    if (isFav) {
      return removeFavorite(adIdStr);
    } else {
      return addFavorite(adIdStr);
    }
  }, [favIds, addFavorite, removeFavorite]);

  // Check if an ad is favorited
  const isFavorite = useCallback((adId) => {
    return favIds.has(String(adId));
  }, [favIds]);

  const value = useMemo(() => ({
    loading,
    loaded,
    error,
    favIds,
    favAds,
    isFavorite,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    refreshFavorites,
  }), [loading, loaded, error, favIds, favAds, isFavorite, toggleFavorite, addFavorite, removeFavorite, refreshFavorites]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}

export default FavoritesContext;
